/* ============================================================================
   Hours panel — the body of the request-detail "Hours" tab.
   - Designers (the request's assigned designer) log time entries.
   - Requesters (the request's requester) approve or adjust each entry.
   Approving/adjusting locks the entry; approved_hours is the billable record.
   HOURS ONLY — no rates, no dollar amounts anywhere. Design: Direction-C
   co-brand, one orange CTA (Log hours); review actions are navy / ghost.
   ============================================================================ */

import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import { apiGet, apiPost, apiPut, apiDelete, isApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth';

interface RequestMeta {
  id: number;
  requesterId: number;
  assignedDesignerId: number | null;
}

interface Entry {
  id: number;
  requestId: number;
  designerId: number;
  designerName: string | null;
  entryDate: string;
  hours: number;
  notes: string | null;
  statusCode: string;
  statusName: string;
  approvedHours: number | null;
  adjustReason: string | null;
  isLocked: boolean;
  reviewedById: number | null;
  reviewedByName: string | null;
  reviewedAt: string | null;
  createdAt: string;
}

interface HoursResponse {
  request: RequestMeta;
  entries: Entry[];
}

const ADMIN_ROLES = ['Admin', 'SystemAdmin'];

function statusPillClass(code: string): string {
  switch (code) {
    case 'approved':
      return 'pill pill-success';
    case 'adjusted':
      return 'pill pill-info';
    default:
      return 'pill pill-warning';
  }
}

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

function fmtHours(n: number | null | undefined): string {
  return typeof n === 'number' ? n.toFixed(2) : '—';
}

export default function HoursPanel({ requestId }: { requestId: number }) {
  const { user } = useAuth();
  const [data, setData] = useState<HoursResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Log form (designer)
  const [logDate, setLogDate] = useState<string>(today());
  const [logHours, setLogHours] = useState<string>('');
  const [logNotes, setLogNotes] = useState<string>('');
  const [logBusy, setLogBusy] = useState(false);
  const [logError, setLogError] = useState<string | null>(null);

  // Adjust form (requester) — which entry is being adjusted
  const [adjustId, setAdjustId] = useState<number | null>(null);
  const [adjustHours, setAdjustHours] = useState<string>('');
  const [adjustReason, setAdjustReason] = useState<string>('');
  const [rowBusy, setRowBusy] = useState<number | null>(null);
  const [rowError, setRowError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiGet<HoursResponse>(`/api/hours?requestId=${requestId}`);
      setData(res);
    } catch (e) {
      setError(isApiError(e) ? e.message : 'Failed to load hours.');
    } finally {
      setLoading(false);
    }
  }, [requestId]);

  useEffect(() => {
    void load();
  }, [load]);

  const isAdmin = !!user && ADMIN_ROLES.includes(user.role);
  const canLog =
    !!user &&
    !!data &&
    (isAdmin || (user.role === 'Designer' && data.request.assignedDesignerId === user.id));
  const canReview =
    !!user &&
    !!data &&
    (isAdmin || (user.role === 'Requester' && data.request.requesterId === user.id));

  // Delete: the designer who logged an entry may remove their own; admins remove any.
  const canDeleteEntry = (e: Entry): boolean =>
    isAdmin || (user?.role === 'Designer' && e.designerId === user.id);

  const entries = data?.entries ?? [];

  const totals = useMemo(() => {
    let logged = 0;
    let billable = 0;
    for (const e of entries) {
      logged += Number(e.hours) || 0;
      if (e.approvedHours != null) billable += Number(e.approvedHours) || 0;
    }
    return { logged, billable };
  }, [entries]);

  async function submitLog(evt: FormEvent) {
    evt.preventDefault();
    setLogError(null);
    const hours = parseFloat(logHours);
    if (!logDate) return setLogError('Pick a date.');
    if (!Number.isFinite(hours) || hours <= 0 || hours > 24) {
      return setLogError('Enter hours greater than 0 and at most 24.');
    }
    setLogBusy(true);
    try {
      await apiPost('/api/hours', {
        requestId,
        entryDate: logDate,
        hours,
        notes: logNotes.trim() || undefined,
      });
      setLogHours('');
      setLogNotes('');
      setLogDate(today());
      await load();
    } catch (e) {
      setLogError(isApiError(e) ? e.message : 'Could not log hours.');
    } finally {
      setLogBusy(false);
    }
  }

  async function approve(id: number) {
    setRowError(null);
    setRowBusy(id);
    try {
      await apiPut(`/api/hours/${id}/approve`);
      await load();
    } catch (e) {
      setRowError(isApiError(e) ? e.message : 'Could not approve entry.');
    } finally {
      setRowBusy(null);
    }
  }

  function beginAdjust(e: Entry) {
    setRowError(null);
    setAdjustId(e.id);
    setAdjustHours(String(e.hours));
    setAdjustReason('');
  }

  function cancelAdjust() {
    setAdjustId(null);
    setAdjustHours('');
    setAdjustReason('');
  }

  async function submitAdjust(id: number) {
    setRowError(null);
    const hours = parseFloat(adjustHours);
    if (!Number.isFinite(hours) || hours < 0 || hours > 24) {
      return setRowError('Adjusted hours must be between 0 and 24.');
    }
    if (!adjustReason.trim()) {
      return setRowError('A reason is required to adjust.');
    }
    setRowBusy(id);
    try {
      await apiPut(`/api/hours/${id}/adjust`, { hours, reason: adjustReason.trim() });
      cancelAdjust();
      await load();
    } catch (e) {
      setRowError(isApiError(e) ? e.message : 'Could not adjust entry.');
    } finally {
      setRowBusy(null);
    }
  }

  async function del(e: Entry): Promise<void> {
    if (!window.confirm(`Delete this ${fmtHours(e.hours)}-hour entry from ${e.entryDate}? This cannot be undone.`)) {
      return;
    }
    setRowError(null);
    setRowBusy(e.id);
    try {
      await apiDelete(`/api/hours/${e.id}`);
      await load();
    } catch (err) {
      setRowError(isApiError(err) ? err.message : 'Could not delete entry.');
    } finally {
      setRowBusy(null);
    }
  }

  if (loading) {
    return (
      <div className="card">
        <div className="row">
          <span className="spinner" />
          <span className="muted">Loading hours…</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card empty">
        <h3>Couldn’t load hours</h3>
        <p className="muted">{error}</p>
        <button className="btn btn-navy btn-sm" onClick={() => void load()}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="stack">
      {/* Log form — assigned designer (or admin) */}
      {canLog && (
        <div className="card">
          <div className="card-header">
            <h3>Log hours</h3>
            <span className="meta">Logged against this request only.</span>
          </div>
          <form onSubmit={submitLog}>
            <div className="grid" style={{ gridTemplateColumns: '160px 140px 1fr auto', alignItems: 'end' }}>
              <div className="field" style={{ marginBottom: 0 }}>
                <label htmlFor="hrs-date">Work date</label>
                <input
                  id="hrs-date"
                  className="in"
                  type="date"
                  max={today()}
                  value={logDate}
                  onChange={(e) => setLogDate(e.target.value)}
                />
              </div>
              <div className="field" style={{ marginBottom: 0 }}>
                <label htmlFor="hrs-hours">Hours</label>
                <input
                  id="hrs-hours"
                  className="in mono"
                  type="number"
                  min="0.25"
                  max="24"
                  step="0.25"
                  placeholder="0.00"
                  value={logHours}
                  onChange={(e) => setLogHours(e.target.value)}
                />
              </div>
              <div className="field" style={{ marginBottom: 0 }}>
                <label htmlFor="hrs-notes">Note</label>
                <input
                  id="hrs-notes"
                  className="in"
                  type="text"
                  maxLength={600}
                  placeholder="What was worked on (optional)"
                  value={logNotes}
                  onChange={(e) => setLogNotes(e.target.value)}
                />
              </div>
              <button className="btn btn-primary" type="submit" disabled={logBusy}>
                {logBusy ? 'Logging…' : 'Log hours'}
              </button>
            </div>
            {logError && <p className="error-text" style={{ marginTop: 10 }}>{logError}</p>}
          </form>
        </div>
      )}

      {/* Entries + approval panel */}
      <div className="card">
        <div className="card-header">
          <h3>Time entries</h3>
          <div className="row" style={{ gap: 16 }}>
            <span className="meta">
              Logged <span className="mono">{fmtHours(totals.logged)}</span>
            </span>
            <span className="meta">
              Billable <span className="mono">{fmtHours(totals.billable)}</span>
            </span>
          </div>
        </div>

        {rowError && <p className="error-text" style={{ marginBottom: 12 }}>{rowError}</p>}

        {entries.length === 0 ? (
          <div className="empty">
            <h3>No hours logged yet</h3>
            <p className="muted">
              {canLog
                ? 'Use the form above to log the first time entry.'
                : 'The assigned designer hasn’t logged any time yet.'}
            </p>
          </div>
        ) : (
          <div className="table-wrap">
            <table className="tbl tbl-compact">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Designer</th>
                  <th style={{ textAlign: 'right' }}>Hours</th>
                  <th>Status</th>
                  <th style={{ textAlign: 'right' }}>Billable</th>
                  <th>Note / reason</th>
                  <th style={{ textAlign: 'right' }}>Review</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((e) => {
                  const editing = adjustId === e.id;
                  return (
                    <tr key={e.id}>
                      <td className="mono">{e.entryDate}</td>
                      <td>{e.designerName ?? `#${e.designerId}`}</td>
                      <td className="mono" style={{ textAlign: 'right' }}>{fmtHours(e.hours)}</td>
                      <td>
                        <span className={statusPillClass(e.statusCode)}>{e.statusName}</span>
                      </td>
                      <td className="mono" style={{ textAlign: 'right' }}>{fmtHours(e.approvedHours)}</td>
                      <td>
                        {e.adjustReason ? (
                          <span>
                            <span className="meta">Adjusted:</span> {e.adjustReason}
                          </span>
                        ) : (
                          <span className="muted">{e.notes ?? '—'}</span>
                        )}
                      </td>
                      <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                        {!editing && (
                          <div
                            className="row"
                            style={{ justifyContent: 'flex-end', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}
                          >
                            {e.isLocked ? (
                              <span className="meta">
                                {e.reviewedByName ? `by ${e.reviewedByName}` : 'Locked'}
                              </span>
                            ) : canReview ? (
                              <>
                                <button
                                  className="btn btn-navy btn-sm"
                                  disabled={rowBusy === e.id}
                                  onClick={() => void approve(e.id)}
                                >
                                  Approve
                                </button>
                                <button
                                  className="btn btn-ghost btn-sm"
                                  disabled={rowBusy === e.id}
                                  onClick={() => beginAdjust(e)}
                                >
                                  Adjust
                                </button>
                              </>
                            ) : !canDeleteEntry(e) ? (
                              <span className="meta">Pending</span>
                            ) : null}
                            {canDeleteEntry(e) && (
                              <button
                                className="btn btn-ghost btn-sm"
                                style={{ color: '#b3261e' }}
                                disabled={rowBusy === e.id}
                                title="Delete this time entry"
                                onClick={() => void del(e)}
                              >
                                Delete
                              </button>
                            )}
                          </div>
                        )}

                        {editing && (
                          <div
                            className="row"
                            style={{ justifyContent: 'flex-end', gap: 6, flexWrap: 'wrap' }}
                          >
                            <input
                              className="in mono"
                              type="number"
                              min="0"
                              max="24"
                              step="0.25"
                              aria-label="Adjusted hours"
                              value={adjustHours}
                              onChange={(ev) => setAdjustHours(ev.target.value)}
                              style={{ width: 90, height: 30 }}
                            />
                            <input
                              className="in"
                              type="text"
                              maxLength={600}
                              aria-label="Reason"
                              placeholder="Reason"
                              value={adjustReason}
                              onChange={(ev) => setAdjustReason(ev.target.value)}
                              style={{ width: 180, height: 30 }}
                            />
                            <button
                              className="btn btn-navy btn-sm"
                              disabled={rowBusy === e.id}
                              onClick={() => void submitAdjust(e.id)}
                            >
                              {rowBusy === e.id ? 'Saving…' : 'Save'}
                            </button>
                            <button
                              className="btn btn-ghost btn-sm"
                              disabled={rowBusy === e.id}
                              onClick={cancelAdjust}
                            >
                              Cancel
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={2} className="meta" style={{ fontWeight: 600 }}>
                    Total
                  </td>
                  <td className="mono" style={{ textAlign: 'right', fontWeight: 700 }}>
                    {fmtHours(totals.logged)}
                  </td>
                  <td />
                  <td className="mono" style={{ textAlign: 'right', fontWeight: 700 }}>
                    {fmtHours(totals.billable)}
                  </td>
                  <td colSpan={2} />
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
